import { Store } from '@ngxs/store';
import { combineLatest, defaultIfEmpty, defer, filter, Observable } from 'rxjs';
import { map, scan, shareReplay, switchMap, take, tap, first } from 'rxjs/operators';

import {
  BaseClient,
  ClientRequestDocumentUploadModel,
  ClientRequestGroupType,
  ClientRequestModel,
  ClientRequestStatus,
  RejectedClientRequestItem,
  RequestItem,
  RequestSection,
  RequestSubmissionFile,
  StaffEntity,
} from '@aiwyn/api';
import { Avatar, distinctUntilObjectChanged, getLoadedResults, ignoreNullish, isEmpty, Loadable, Logger, BaseContact, LoadableStatus } from '@aiwyn/shared';

import { ClientRequestAssigneeModel, ClientRequestStatusPill, ClientRequestStatusPillEnum, TypedIdentity } from '../models';
import { CommonClientRequestsState } from '../state';
import { ClientRequestIdentityHelper, RequestIdentityType } from '../utility';

import { AbstractBaseCommonClientRequestController } from './abstract-base-common-client-request-controller.model';
import { BaseCommonClientRequestAssignee } from './common-client-request-assignee';
import { BaseCommonClientRequestClient } from './common-client-request-client';

export class BaseCommonClientRequest<
  ClientRequest extends ClientRequestModel = ClientRequestModel,
  ClientRequestTypeClass extends CommonRequestModelToClassMap<ClientRequest> = CommonRequestModelToClassMap<ClientRequest>,
> extends AbstractBaseCommonClientRequestController<TypedIdentity<RequestIdentityType>, ClientRequest, ClientRequestTypeClass> {
  model$: Observable<Loadable<ClientRequest>> = defer(() => {
    const requestId = ClientRequestIdentityHelper.parseRequestIdentity(this.identity).id;
    return this._store.select(CommonClientRequestsState.getClientRequestLoadable(requestId)) as Observable<Loadable<ClientRequest>>;
  }).pipe(
    ignoreNullish(),
    shareReplay(1),
  );

  protected _create$(
    model$: Observable<Loadable<ClientRequest>>,
    store: Store,
    logger: Logger,
  ): Observable<ClientRequestTypeClass> {
    return model$.pipe(
      ignoreNullish(),
      take(1),
      map(type => {
        return new CommonClientRequestDocumentUpload(
          model$ as Observable<Loadable<ClientRequestDocumentUploadModel>>,
          store,
          logger,
        ) as ClientRequestTypeClass;
      }),
    );
  }

  name$: Observable<string> = this.model$.pipe(
    map(({ data }) => data?.name),
    shareReplay(1),
  );

  description$: Observable<string> = this.model$.pipe(
    map(({ data }) => data?.description),
  );

  status$: Observable<ClientRequestStatus> = this.model$.pipe(
    map(({ data }) => data?.status),
  );

  isLocked$: Observable<boolean> = this.model$.pipe(
    map(({ data }) => (data?.projectLockdowns?.length ?? 0) > 0),
    shareReplay(1),
  );

  statusPill$: Observable<ClientRequestStatusPill> = this.model$.pipe(
    map(({ data }) => data?.status),
    map((status) => {
      if (status === ClientRequestStatus.DONE) {
        return { status: ClientRequestStatusPillEnum.DONE, color: 'green' } as ClientRequestStatusPill;
      } else if (status === ClientRequestStatus.DRAFT || status === ClientRequestStatus.SENT) {
        return { status: ClientRequestStatusPillEnum.NOT_STARTED, color: 'gray' } as ClientRequestStatusPill;
      } else if (status === ClientRequestStatus.IN_PROGRESS) {
        return { status: ClientRequestStatusPillEnum.IN_PROGRESS, color: 'yellow' } as ClientRequestStatusPill;
      } else if (status === ClientRequestStatus.IN_REVIEW) {
        return { status: ClientRequestStatusPillEnum.IN_REVIEW, color: 'orange' } as ClientRequestStatusPill;
      }
    }),
  );

  rejectedItems$: Observable<RejectedClientRequestItem[]> = this.model$.pipe(
    map(({ data }) => data?.rejectedItems),
  );

  nextDueDate$: Observable<string> = this.model$.pipe(
    map(({ data }) => data?.nextDueDate),
  );

  nextDocumentDue$: Observable<string> = this.model$.pipe(
    map(({ data }) => data?.nextDocumentDue),
  );

  requestItems$: Observable<RequestItem[]> = this.model$.pipe(
    map(({ data }) => data?.requestItems || []),
  );

  completedAt$: Observable<string> = this.model$.pipe(
    map(({ data }) => data?.completedAt),
  );

  assignees$: Observable<BaseCommonClientRequestAssignee[]> = this.model$.pipe(
    map(({ data }) => data.assignees.map(({ contactId }) =>
      ClientRequestIdentityHelper.getAssigneeIdentity({ id: contactId } as ClientRequestAssigneeModel),
    )),
    distinctUntilObjectChanged(),
    scan(
      (existingAssignees, assigneeIdentities) => {
        const newAssigneeIdentities = assigneeIdentities.filter(assigneeIdentity =>
          !existingAssignees.some(({ identity }) => identity === assigneeIdentity),
        );

        return existingAssignees.concat(newAssigneeIdentities.map(assigneeIdentity =>
          new BaseCommonClientRequestAssignee(assigneeIdentity, this._store, this._logger),
        ));
      },
      [] as BaseCommonClientRequestAssignee[],
    ),
    shareReplay(1),
  );

  avatars$: Observable<Avatar[]> = this.assignees$.pipe(
    filter(assignees => !isEmpty(assignees)),
    switchMap(assignees => combineLatest(
      assignees.map(assignee => assignee.typeClass$.pipe(
        switchMap(assigneeTypeClass => combineLatest([
          assigneeTypeClass.name$.pipe(ignoreNullish()),
          assigneeTypeClass.email$.pipe(ignoreNullish()),

        ])),
        map(([name, email]) => ({ identity: assignee.identity, name, email })),
        map(contact => {
          const fullName = contact.name;
          const firstName = fullName.split(' ')[0];
          const lastName = fullName.split(' ').pop();
          const parsedIdentity = ClientRequestIdentityHelper.parseAssigneeIdentity(contact.identity);

          return {
            id: parsedIdentity.id,
            firstName,
            lastName,
            fullName
          } as Avatar;
        })
      ))
    )),
    defaultIfEmpty([])
  );

  requestItemsAvatars$: Observable<Avatar[]> = this.requestItems$.pipe(
    map(requestItems => {
      const allAssignees = requestItems.reduce((acc, item) => {
        return acc.concat(item.assignees || []);
      }, []);
      
      // Remove duplicates based on contactId
      const uniqueAssignees = allAssignees.filter((assignee, index, self) => 
        index === self.findIndex(a => a.contactId === assignee.contactId)
      );
      
      return uniqueAssignees.map(assignee => assignee.contactId);
    }),
    switchMap(contactIds => {
      if (contactIds.length === 0) {
        return [[]];
      }
      
      // First, wait for assignees to be loaded in the state
      return this._store.select(CommonClientRequestsState.getAllAssigneesStatus).pipe(
        filter(status => status === LoadableStatus.LOADED),
        first(),
        switchMap(() => {
          const contactObservables = contactIds.map(contactId =>
            this._store.select(CommonClientRequestsState.getAssigneeLoadableById(contactId)).pipe(
              filter((loadable: Loadable<BaseContact>) => loadable?.data != null),
              map((loadable: Loadable<BaseContact>) => loadable.data!)
            )
          );
          
          return combineLatest(contactObservables);
        })
      );
    }),
    map((contacts: BaseContact[]) => {
      return contacts.map(contact => {
        const fullName = contact.contactInfo.fullName || '';
        const nameParts = fullName.split(/\s/);
        const firstName = nameParts[0] || 'Unknown';
        const lastName = nameParts.at(-1) || "User";
        
        return {
          id: contact.id,
          firstName,
          lastName,
          fullName
        } as Avatar;
      });
    }),
    defaultIfEmpty([]),
    shareReplay(1)
  );

  client$: Observable<BaseCommonClientRequestClient> = this.data$.pipe(
    map(({ clientId }) => ClientRequestIdentityHelper.getClientIdentity({ id: clientId } as BaseClient)),
    map(client => new BaseCommonClientRequestClient(client, this._store, this._logger)),
    shareReplay(1),
  );

  staff$: Observable<Loadable<StaffEntity>> = this.data$.pipe(
    map(({ reviewerStaffId }) => reviewerStaffId),
    switchMap(staffId => this._store.select(CommonClientRequestsState.getStaffLoadable(staffId))),
  );

  groupIdentity$: Observable<TypedIdentity<ClientRequestGroupType>> = this.data$.pipe(
    map(data => (`${ClientRequestGroupType.JOB}-` + data.jobId) as TypedIdentity<ClientRequestGroupType>),
  );
}

abstract class AbstractCommonClientRequestType<ClientRequest extends ClientRequestModel = ClientRequestModel> {
  constructor(
    protected readonly model$: Observable<Loadable<ClientRequest>>,
    protected readonly _store: Store,
    protected readonly _logger: Logger,
  ) {}
}

export class CommonClientRequestDocumentUpload extends AbstractCommonClientRequestType<ClientRequestDocumentUploadModel> {
  statusDetails$ = this.model$.pipe(
    map(({ data }) => data.progress),
  );

  sections$: Observable<RequestSection[]> = this.model$.pipe(
    map(({ data }) => data.id),
    switchMap(id => getLoadedResults(this._store.select(CommonClientRequestsState.getClientRequestDetailsLoadable(id)), true)),
    map(({ sections }) => sections),
    shareReplay(1),
  );

  files$: Observable<RequestSubmissionFile[]> = this.model$.pipe(
    map(({ data }) => data.id),
    switchMap(id => getLoadedResults(this._store.select(CommonClientRequestsState.getClientRequestMetadataLoadable(id)), true)),
    map(({ files }) => files),
    shareReplay(1),
  );
}

export type CommonRequestModelToClassMap<ClientRequest extends ClientRequestModel> =
  ClientRequest extends ClientRequestDocumentUploadModel
    ? CommonClientRequestDocumentUpload
    : never;